import {Typography, Grid, Box} from '@mui/material';
import {
  GridColumnGroupingModel,
  GridFilterModel,
  GridRenderHeaderFilterProps,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {t} from 'i18next';
import {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {AurDataGridColDef} from '../../../types/types';
import {TrackGAEventParams} from '../../../types/analyticsTypes';
import {
  formatNumberWithComma,
  getUTCDateTimeForDisplay,
  downloadResponseAsXLSX,
} from '../../../utils/utils';
import AurDataGridPro from '../../common/aur-data-grid-pro/AurDataGridPro';
import FilterTextField from '../../common/aur-data-grid-pro/FilterTextField';
import HeaderCard from '../completedmapedData/component/HeaderCard';
import AurButton from '../../common/aur-button/AurButton';
import {FiberManualRecordRounded} from '@mui/icons-material';
import ProjectWorkSpaceSelectionDialog from '../HostingUserSummaryReport/WorkSpaceSummary/ProjectWorkSpaceSelectionDialog';
import ProjectWorkSpaceSelection from '../HostingUserSummaryReport/WorkSpaceSummary/ProjectWorkSpaceSelection';
import {openDialog} from '../../../redux/slices/reportsDialogSlice';
import {ReactComponent as ExchangeIcon} from '../../../../src/assests/svg/icon_shift.svg';
import {ReactComponent as ExportIcon} from '../../../../src/assests/svg/export_icon.svg';
import {AurLightTooltip} from '../../common/aur-light-tooltip/aur-light-tooltip';
import {
  fetchCompletedMappedDataTotals,
  fetchCompletedMappedDataList,
  exportCompletedMappedData,
} from '../../../api/ReportingManagement';
import {
  CompletedMappedDataTotalsResponse,
  MappedDataFilter,
  CompletedMappedDataRow,
  CompletedMappedDataListResponse,
} from '../../../types/completed-mapped-data/completedMappedDataType';
import FieldPopoverGridFilter from '../../pages/field-management/field-table/FieldPopoverGridFilter';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import AurPagination from '../../common/aur-pagination/AurPagination';
import AurDataGridNoRowsOverlay from '../../common/aur-data-grid-no-rows-overlay/AurDataGridNoRowsOverlay';
import AurDataGridLoadingOverlay from '../../common/aur-data-grid-loading-overlay/AurDataGridLoadingOverlay';
import {GridLocaleText} from '@mui/x-data-grid-pro';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_DATA_MODE_AUTO,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
  GA_REPORT_TYPE,
} from '../../../constants/analyticsConstants';

dayjs.extend(utc);

const COLUMN_GROUP_HEADER_HEIGHT = 128;
const COLUMN_HEADER_HEIGHT = 64;
const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const FILTER_DEBOUNCE_MS = 500;

const INPUT_FILTER_FIELDS = ['mediaEntity', 'processingDataset'] as const;

const FILTER_FIELD_MAP: Record<string, string> = {
  dateReceived: 'ReceivedDate',
  mediaEntity: 'MediaEntity',
  processingDataset: 'efile',
  dateMappedCompleted: 'ProcessingComplete',
};

const SORT_FIELD_MAP: Record<string, string> = {
  dateReceived: 'receiveddate',
  processingDataset: 'efile',
};

const DATE_FILTER_ODATA_MAPPING_OBJ: Record<string, string> = {
  is: 'eq',
  before: 'lt',
  onOrBefore: 'le',
  after: 'gt',
  onOrAfter: 'ge',
  NotSet: 'null', // ProcessingComplete eq null = In Progress
};

const customLocaleText = {
  headerFilterOperatorNotSet: 'Not Set',
} as Partial<GridLocaleText>;

const buildApiFilters = (filterModel: GridFilterModel): MappedDataFilter[] => {
  if (!filterModel?.items?.length) return [];

  return filterModel.items
    .filter((item) => {
      if (item.operator === 'NotSet') return true;
      return item.value !== undefined && item.value !== '';
    })
    .map((item) => {
      let value = item.value ?? '';

      const mappedOperator =
        DATE_FILTER_ODATA_MAPPING_OBJ[item.operator ?? ''] ?? 'equal';

      if (value instanceof Date || dayjs.isDayjs(value)) {
        switch (mappedOperator) {
          case 'lt':
            // "date is before 1 JAN" — exclude 1 JAN from response set
            value = dayjs(value).format('YYYY-MM-DDT00:00:00');
            break;
          case 'le':
            // "date is before or on 1 JAN" — include all and results with 1 JAN
            value = dayjs(value).format('YYYY-MM-DDT23:59:59');
            break;
          case 'gt':
            // "date Is after 1 JAN" — exclude all of 1 JAN
            value = dayjs(value).format('YYYY-MM-DDT23:59:59');
            break;
          case 'ge':
            // "date Is after or on 1 JAN" — include from start of 1 JAN
            value = dayjs(value).format('YYYY-MM-DDT00:00:00');
            break;
          case 'eq':
            // "date equal 1 JAN"
            value = dayjs(value).format('YYYY-MM-DD');
            break;
          default:
            value = dayjs(value).format('YYYY-MM-DD');
        }
      }

      return {
        field: FILTER_FIELD_MAP[item.field] ?? item.field,
        operator: mappedOperator,
        value,
      };
    });
};

const FILTER_COLUMN_GA_NAME_KEYS: Record<string, string> = {
  dateReceived:
    'reports.completedMappedData.dataGrid.columnHeaders.dateReceived',
  mediaEntity: 'reports.completedMappedData.dataGrid.columnHeaders.mediaEntity',
  processingDataset:
    'reports.completedMappedData.dataGrid.columnHeaders.processingDataset',
  dateMappedCompleted:
    'reports.completedMappedData.dataGrid.columnHeaders.dateMappedCompleted',
};

const trackDebouncedInputFilterGA = (
  trackGAEvent: (params: TrackGAEventParams) => void,
  model: GridFilterModel,
  lastTrackedInputFilterValues: Record<string, string>
) => {
  INPUT_FILTER_FIELDS.forEach((field) => {
    const item = model.items.find((filterItem) => filterItem.field === field);
    const value = String(item?.value ?? '');
    const lastValue = lastTrackedInputFilterValues[field] ?? '';

    if (value !== '' && value !== lastValue) {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.FILTER,
        click: {
          name: t(
            FILTER_COLUMN_GA_NAME_KEYS[field] ??
              `reports.completedMappedData.dataGrid.columnHeaders.${field}`
          ),
          type: GA_CLICK_TYPES.INPUT,
          region: GA_REGIONS.BODY,
          action: t('gaTracking.reports.filteredMappedDataSetList'),
        },
      });
      lastTrackedInputFilterValues[field] = value;
    } else if (value === '' && lastValue !== '') {
      lastTrackedInputFilterValues[field] = '';
    }
  });
};

const columnDef = (
  apiRef: ReturnType<typeof useGridApiRef>,
  onApplyDropdownFilter: (columnField: string) => void,
  onApplyInputFilter: (columnField: string) => void
): AurDataGridColDef[] => [
  {
    field: 'dateReceived',
    headerName: t(
      'reports.completedMappedData.dataGrid.columnHeaders.dateReceived'
    ),
    flex: 0.85,
    sortable: true,
    isDateColumn: true,
    headerClassName: 'datetime-header-filter-column',
    renderCell: (params) => getUTCDateTimeForDisplay(params.value),

    renderHeaderFilter: () => (
      <FieldPopoverGridFilter
        apiRef={apiRef}
        columnField="dateReceived"
        type="date"
        inputClass="filter-input-field-container"
        inputType="text"
        caretIconContainerClass="caret-dropdown-icon"
        dataTestId="date-received-filter"
        onApplyFilters={onApplyDropdownFilter}
      />
    ),
  },
  {
    field: 'mediaEntity',
    headerName: t(
      'reports.completedMappedData.dataGrid.columnHeaders.mediaEntity'
    ),
    headerClassName: 'datetime-header-filter-column',
    flex: 1,
    sortable: true,
    renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
      <FilterTextField
        gridRenderHeaderFilterProps={params}
        variant="outlined"
        fullWidth
        placeholder="Filter"
        onApplyFilter={onApplyInputFilter}
      />
    ),
  },
  {
    field: 'processingDataset',
    headerName: t(
      'reports.completedMappedData.dataGrid.columnHeaders.processingDataset'
    ),
    headerClassName: 'datetime-header-filter-column',
    flex: 0.65,
    sortable: true,
    renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
      <FilterTextField
        gridRenderHeaderFilterProps={params}
        variant="outlined"
        fullWidth
        placeholder="Filter"
        onApplyFilter={onApplyInputFilter}
      />
    ),
  },
  {
    field: 'recordsMapped',
    headerName: t(
      'reports.completedMappedData.dataGrid.columnHeaders.recordsMapped'
    ),
    flex: 0.65,
    sortable: true,
    filterable: false,
    renderHeaderFilter: () => (
      <span className="sr-only">
        {t('reports.completedMappedData.dataGrid.columnHeaders.recordsMapped')}
      </span>
    ),
  },
  {
    field: 'nativeCount',
    headerName: t(
      'reports.completedMappedData.dataGrid.columnHeaders.nativeCount'
    ),
    flex: 0.75,
    sortable: true,
    filterable: false,
    renderHeaderFilter: () => (
      <span className="sr-only">
        {t('reports.completedMappedData.dataGrid.columnHeaders.nativeCount')}
      </span>
    ),
  },
  {
    field: 'textCount',
    headerName: t(
      'reports.completedMappedData.dataGrid.columnHeaders.textCount'
    ),
    flex: 0.75,
    sortable: true,
    filterable: false,
    renderHeaderFilter: () => (
      <span className="sr-only">
        {t('reports.completedMappedData.dataGrid.columnHeaders.textCount')}
      </span>
    ),
  },
  {
    field: 'dateMappedCompleted',
    headerName: t(
      'reports.completedMappedData.dataGrid.columnHeaders.dateMappedCompleted'
    ),
    flex: 1.1,
    sortable: true,
    isDateColumn: true,
    headerClassName: 'datetime-header-filter-column',
    renderHeaderFilter: () => (
      <FieldPopoverGridFilter
        apiRef={apiRef}
        columnField="dateMappedCompleted"
        type="date"
        inputClass="filter-input-field-container"
        inputType="text"
        caretIconContainerClass="caret-dropdown-icon"
        dataTestId="date-mapped-completed-filter"
        excludeDateFilterConditions={[]}
        applyNotSetToFilter={true}
        onApplyFilters={onApplyDropdownFilter}
      />
    ),
    renderCell: (params) =>
      params.value ? (
        <AurLightTooltip title={t('common.yearMonthDate')}>
          <span>{getUTCDateTimeForDisplay(params.value)}</span>
        </AurLightTooltip>
      ) : (
        <AurButton
          type="text"
          startIcon={
            <FiberManualRecordRounded
              sx={{color: 'success.main', fontSize: 13.33}}
              aria-hidden="true"
            />
          }
        >
          <span style={{color: '#fcfcfd'}}>
            {t('common.statusTypes.inProgress')}
          </span>
        </AurButton>
      ),
  },
];

const CompeletedMappedDataReportPage = () => {
  const {
    selectedProjectName: projectName,
    selectedWorkSpaceName: workspaceName,
    refreshDates,
    projectAlphaCode,
    currentProject: selectedProject,
    clientId,
    projectId,
    workspaceId,
  } = useSelector((state: RootState) => state.reports);
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const {trackGAEvent} = useAnalytics();

  const [totals, setTotals] = useState<CompletedMappedDataTotalsResponse>({
    totalVolumesMapped: 0,
    recordsMapped: 0,
    aggregateNative: 0,
    aggregateNativeSize: 0,
    aggregateText: 0,
    aggregateTextSize: 0,
  });
  const [rows, setRows] = useState<any[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {field: 'dateReceived', sort: 'desc'},
  ]);
  const [filterCount, setFilterCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const isPageViewTrackedRef = useRef(false);
  const apiResponseTime = useRef('');
  const filterModelRef = useRef<GridFilterModel>({items: []});
  const filterDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const inputFilterGATimer = useRef<NodeJS.Timeout | null>(null);
  const lastTrackedInputFilterValuesRef = useRef<Record<string, string>>({});

  const gridColumnGroupingModel: GridColumnGroupingModel = [
    {
      groupId: 'Completed Mapped Data Report Data Grid Group Header',
      children: [
        {field: 'dateReceived'},
        {field: 'mediaEntity'},
        {field: 'processingDataset'},
        {field: 'recordsMapped'},
        {field: 'nativeCount'},
        {field: 'textCount'},
        {field: 'dateMappedCompleted'},
      ],
      renderHeaderGroup: () => (
        <Box sx={{flexGrow: 1}}>
          <Grid
            container
            alignItems="center"
            className="cmd-grid-group-header-container"
          >
            <Grid item xs className="header-content">
              <Typography className="typo-text subhead3_size header">
                {t(
                  'reports.completedMappedData.dataGrid.columnGroupHeader.header'
                )}
              </Typography>
              <Typography className="typo-text body2_size sub-header">
                {t(
                  'reports.completedMappedData.dataGrid.columnGroupHeader.description'
                )}
              </Typography>
            </Grid>
            <Grid item xs="auto" className="header-button">
              <AurButton
                ariaLabel={t(
                  'reports.completedMappedData.dataGrid.columnGroupHeader.exportButton'
                )}
                classNames="typo-text buttonMedium_size"
                onClick={exportReport}
                type="continue"
                size="Medium"
                startIcon={<ExportIcon aria-hidden />}
                dataTestId="export-cmd-report-button"
              >
                {t(
                  'reports.completedMappedData.dataGrid.columnGroupHeader.exportButton'
                )}
              </AurButton>
            </Grid>
          </Grid>
        </Box>
      ),
    },
  ];

  const loadData = useCallback(
    async (
      filters: MappedDataFilter[],
      currentPage: number,
      currentPageSize: number,
      currentSortModel: GridSortModel
    ) => {
      if (!clientId || !projectId || !workspaceId) return;

      setLoading(true);

      const sort = currentSortModel.map((s) => ({
        field: SORT_FIELD_MAP[s.field] ?? s.field,
        direction: (s.sort ?? 'desc') as 'asc' | 'desc',
      }));

      try {
        const [totalsRes, listRes] = await Promise.all([
          fetchCompletedMappedDataTotals({
            clientId,
            projectId,
            workspaceId,
            filters,
          }),
          fetchCompletedMappedDataList({
            clientId,
            projectId,
            workspaceId,
            pageNumber: currentPage + 1,
            pageSize: currentPageSize,
            filters,
            sort,
          }),
        ]);

        setTotals(totalsRes.data.data as CompletedMappedDataTotalsResponse);

        const mapped = (
          (listRes.data.data as CompletedMappedDataRow[]) ?? []
        ).map((row: CompletedMappedDataRow, idx: number) => ({
          id: idx,
          dateReceived: row.dateReceived ?? '',
          mediaEntity: row.mediaEntity ?? '',
          processingDataset: row.processingDataset ?? '',
          recordsMapped: formatNumberWithComma(row.recordsMapped ?? 0),
          nativeCount: `${formatNumberWithComma(row.nativeCount ?? 0)} (${formatNumberWithComma(Number(row.nativeGBSize ?? 0).toFixed(1), 1)})`,
          textCount: `${formatNumberWithComma(row.textCount ?? 0)} (${formatNumberWithComma(Number(row.textGBSize ?? 0).toFixed(1), 1)})`,
          dateMappedCompleted: row.dateMappedCompleted ?? null,
        }));

        setRows(mapped);
        setTotalItems(
          (listRes.data.meta as CompletedMappedDataListResponse['meta'])
            ?.pagination?.totalItems ?? 0
        );
        apiResponseTime.current =
          listRes.data.responseTime ?? totalsRes.data.responseTime ?? '';
      } catch (err) {
        console.error('Failed to load completed mapped data', err);
        apiResponseTime.current =
          (err as {responseTime?: string}).responseTime ?? '';
      } finally {
        setLoading(false);
        if (!isPageViewTrackedRef.current) {
          isPageViewTrackedRef.current = true;
          trackGAEvent({
            eventName: GA_EVENT_NAMES.PAGE_VIEW,
            page: GA_DATA_MODE_AUTO,
            user: GA_DATA_MODE_AUTO,
            report: {
              name: t('gaTracking.reports.completedMappedDataReport'),
              type: GA_REPORT_TYPE.STANDARD,
              load_time: apiResponseTime.current,
            },
          });
        }
      }
    },
    [clientId, projectId, workspaceId, trackGAEvent]
  );

  const trackGAEventRef = useRef(trackGAEvent);
  const loadDataRef = useRef(loadData);
  const pageSizeRef = useRef(pageSize);
  const sortModelRef = useRef(sortModel);

  trackGAEventRef.current = trackGAEvent;
  loadDataRef.current = loadData;
  pageSizeRef.current = pageSize;
  sortModelRef.current = sortModel;

  useEffect(() => {
    setPage(0);
    filterModelRef.current = {items: []};
    apiRef.current?.setFilterModel({items: []});
    loadData([], 0, pageSize, sortModel);
  }, [clientId, projectId, workspaceId]);

  const noDataGridOverlay = useMemo(() => {
    if (loading) return AurDataGridLoadingOverlay;
    return AurDataGridNoRowsOverlay;
  }, [loading]) as React.JSXElementConstructor<unknown>;

  const handleFilterModelChange = useCallback((model: GridFilterModel) => {
    filterModelRef.current = model;

    const uniqueFields = new Set(model.items.map((item) => item.field));
    setFilterCount(uniqueFields.size);

    if (!isPageViewTrackedRef.current && model.items.length === 0) {
      return;
    }

    if (filterDebounceTimer.current) {
      clearTimeout(filterDebounceTimer.current);
    }

    filterDebounceTimer.current = setTimeout(() => {
      setPage(0);
      const filters = buildApiFilters(filterModelRef.current);
      loadDataRef.current(
        filters,
        0,
        pageSizeRef.current,
        sortModelRef.current
      );
    }, FILTER_DEBOUNCE_MS);
  }, []);

  const handleInputFilterApply = useCallback(
    (columnField: string) => {
      if (!(INPUT_FILTER_FIELDS as readonly string[]).includes(columnField)) {
        return;
      }

      if (inputFilterGATimer.current) {
        clearTimeout(inputFilterGATimer.current);
      }

      inputFilterGATimer.current = setTimeout(() => {
        const model =
          apiRef.current?.state?.filter?.filterModel ?? filterModelRef.current;
        filterModelRef.current = model;

        trackDebouncedInputFilterGA(
          trackGAEventRef.current,
          model,
          lastTrackedInputFilterValuesRef.current
        );
      }, FILTER_DEBOUNCE_MS);
    },
    [apiRef]
  );

  useEffect(() => {
    return () => {
      if (filterDebounceTimer.current) {
        clearTimeout(filterDebounceTimer.current);
      }
      if (inputFilterGATimer.current) {
        clearTimeout(inputFilterGATimer.current);
      }
    };
  }, []);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
      const filters = buildApiFilters(filterModelRef.current);
      loadData(filters, newPage - 1, pageSize, sortModel);
    },
    [loadData, pageSize, sortModel]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setPageSize(newPageSize);
      setCurrentPage(1);
      const filters = buildApiFilters(filterModelRef.current);
      loadData(filters, 0, newPageSize, sortModel);
    },
    [loadData, sortModel]
  );

  const handleSortChange = useCallback(
    (model: GridSortModel) => {
      const filtered = model.filter((s) => s.sort);
      setSortModel(filtered);
      const filters = buildApiFilters(filterModelRef.current);
      loadData(filters, page, pageSize, filtered);
    },
    [loadData, page, pageSize]
  );

  const handleClearFilters = useCallback(() => {
    apiRef.current?.setFilterModel({items: []});
  }, [apiRef]);

  const trackColumnFilter = useCallback(
    (columnField: string, clickType: string) => {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.FILTER,
        click: {
          name: t(
            FILTER_COLUMN_GA_NAME_KEYS[columnField] ??
              `reports.completedMappedData.dataGrid.columnHeaders.${columnField}`
          ),
          type: clickType,
          region: GA_REGIONS.BODY,
          action: t('gaTracking.reports.filteredMappedDataSetList'),
        },
      });
    },
    [trackGAEvent]
  );

  const handleApplyDropdownFilter = useCallback(
    (columnField: string) => {
      trackColumnFilter(columnField, GA_CLICK_TYPES.DROPDOWN);
    },
    [trackColumnFilter]
  );

  const exportReport = async () => {
    if (!clientId || !projectId || !workspaceId) {
      return;
    }

    const filters = buildApiFilters(filterModelRef.current);

    const sort = sortModel.map((s) => ({
      field: SORT_FIELD_MAP[s.field] ?? s.field,
      direction: (s.sort ?? 'desc') as 'asc' | 'desc',
    }));

    try {
      const excelData = await exportCompletedMappedData({
        clientId,
        projectId,
        workspaceId,
        filters,
        sort,
      });

      const now = dayjs(Date.now()).format('YYYYMMDD');
      downloadResponseAsXLSX(excelData, `Summary_Mapped_Data_Report_${now}`);

      trackGAEvent({
        eventName: GA_EVENT_NAMES.EXPORT,
        click: {
          name: t('gaTracking.reports.exportMappedDataReport'),
          type: GA_CLICK_TYPES.BUTTON,
          region: GA_REGIONS.BODY,
          action: t('gaTracking.reports.exportedMappedDataSetsReport'),
        },
        file_extension: GA_FILE_EXTENTIONS.XLSX,
      });
    } catch (err) {
      console.error('Failed to export completed mapped data report', err);
    }
  };

  const paginationBlock = useMemo(
    () => (
      <div className="table-pagination-container">
        <AurPagination
          totalItems={totalItems}
          itemsPerPage={pageSize}
          currentPage={currentPage}
          perPageOptions={PAGE_SIZE_OPTIONS}
          itemType={t('common.reports')}
          setItemsPerPage={handlePageSizeChange}
          onPageChange={handlePageChange}
          pageItemDropDownItemColor="#FCFCFD"
          className="cmd-pagination"
        />
      </div>
    ),
    [
      totalItems,
      pageSize,
      currentPage,
      filterCount,
      handlePageSizeChange,
      handlePageChange,
      handleClearFilters,
    ]
  );

  const handleOpenDialog = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.reports.projectWorkspace'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.reports.changeProjectandWorkspaceBegin'),
      },
    });
    dispatch(openDialog());
  };

  const columns = useMemo(
    () => columnDef(apiRef, handleApplyDropdownFilter, handleInputFilterApply),
    [apiRef, handleApplyDropdownFilter, handleInputFilterApply]
  );

  return (
    <div role="region" aria-label={t('reports.completedMappedData.tabHeader')}>
      <div className="completed-mapped-data-page">
        {/*Header Section*/}
        <div className={'completed-mapped-data-header'}>
          <div className="cmd-header-title-container">
            <Typography
              role="heading"
              aria-level={1}
              className="typo-text subhead3_size cmd-header"
            >
              {projectName}
            </Typography>
            <div className="cmd-sub-header">
              <div>
                <Typography
                  role="heading"
                  aria-level={2}
                  className="typo-text display3_size"
                >
                  {projectAlphaCode} {workspaceName}
                </Typography>
              </div>

              <AurButton
                ariaLabel={`${t('reports.inTakeAuditTab.header.projectButton')} | ${t('reports.inTakeAuditTab.header.workspaceButton')}`}
                classNames="typo-text buttonMedium_size"
                onClick={handleOpenDialog}
                type="continue"
                size="Medium"
                startIcon={<ExchangeIcon aria-hidden />}
                dataTestId="open-project-workspace-dialog"
              >
                {t('reports.inTakeAuditTab.header.projectButton')}{' '}
                <span className="color-button-divider"> | </span>{' '}
                {t('reports.inTakeAuditTab.header.workspaceButton')}{' '}
              </AurButton>
              <ProjectWorkSpaceSelectionDialog
                ContentComponent={ProjectWorkSpaceSelection}
                openFrom={t('reports.projectOverview.tabs.tabIntakeAudit')}
                hideNonConsilioProjects={
                  !selectedProject?.isNonConsilioWorkflow
                }
              />
            </div>
          </div>
          <div className="completed-mapped-date-section">
            <Typography className="typo-text body3_size">
              {t('reports.completedMappedData.header.dateAsOf')}{' '}
              <AurLightTooltip title={t('common.yearMonthDate')}>
                <span className="typo-text label1_size cmd-date-sub-section">
                  {getUTCDateTimeForDisplay(
                    refreshDates.completedMappedData || ''
                  )}
                </span>
              </AurLightTooltip>
            </Typography>
            <Typography className="typo-text body3_size ">
              {t('reports.completedMappedData.header.description')}
            </Typography>
          </div>
        </div>
        <div className="cmd-body-container">
          <div className="cmd-body-card-grid">
            <div className="cmd-card-grid-header">
              <Typography
                role="heading"
                aria-level={2}
                className="typo-text subhead1_size header"
              >
                {t('reports.completedMappedData.body.header')}
              </Typography>
              <Typography className="typo-text body2_size subHeader">
                {t('reports.completedMappedData.body.subHeader')}
              </Typography>
            </div>
            {/* Header cards*/}
            <div className="cmd-card-grid-container">
              <HeaderCard
                title="Total Volumes Mapped"
                header={formatNumberWithComma(totals.totalVolumesMapped)}
              />
              <HeaderCard
                title="Records Mapped"
                header={formatNumberWithComma(totals.recordsMapped)}
              />
              <HeaderCard
                title="Aggregate Native"
                header={formatNumberWithComma(totals.aggregateNative)}
                subheader={`${formatNumberWithComma(Number(totals.aggregateNativeSize).toFixed(1), 1)} GB`}
              />
              <HeaderCard
                title="Aggregate Text"
                header={formatNumberWithComma(totals.aggregateText)}
                subheader={`${formatNumberWithComma(Number(totals.aggregateTextSize).toFixed(1), 1)} GB`}
              />
            </div>
          </div>

          {filterCount > 0 && (
            <div
              className="apply-filter-button-container"
              onClick={handleClearFilters}
              data-testid="cmd-clear-filter"
            >
              <AurButton
                type="exit"
                size="Small"
                label={`${t('fieldManagement.fieldTable.clearFilters')} (${filterCount})`}
                ariaLabel={`${t('fieldManagement.fieldTable.clearFilters')} (${filterCount})`}
              />
            </div>
          )}

          <div className="cmd-grid-pagination-wrapper">
            <AurDataGridPro
              aria-label={t(
                'reports.completedMappedData.dataGrid.columnGroupHeader.header'
              )}
              columns={columns}
              rows={rows}
              getRowId={(row) => row.id}
              checkboxSelection={false}
              headerFilters
              visibleToolTip
              columnGroupingModel={gridColumnGroupingModel}
              columnGroupHeaderHeight={COLUMN_GROUP_HEADER_HEIGHT}
              columnHeaderHeight={COLUMN_HEADER_HEIGHT}
              loading={loading}
              apiRef={apiRef}
              onFilterModelChange={handleFilterModelChange}
              enableServerSide={true}
              isReqLoading={loading}
              hideFooter
              slots={{
                headerFilterMenu: null,
                columnHeaderFilterIconButton: () => null,
                noRowsOverlay: noDataGridOverlay,
                noResultsOverlay: noDataGridOverlay,
              }}
              slotProps={{
                columnHeaders: {className: 'cmd-datagrid-col-header-row'},
                headerFilterCell: {
                  showClearIcon: false,
                },
                noRowsOverlay: {
                  enableParentWrapper: false,
                } as unknown as Record<string, unknown>,
                noResultsOverlay: {
                  enableParentWrapper: false,
                } as unknown as Record<string, unknown>,
              }}
              localeText={customLocaleText}
              sortingMode="server"
              sortModel={sortModel}
              className="cmd-datagrid-pro"
              setSortModel={handleSortChange}
              totalRowCount={totalItems}
              currentPage={currentPage}
              itemsPerPage={pageSize}
            />

            {totalItems > DEFAULT_PAGE_SIZE ? paginationBlock : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompeletedMappedDataReportPage;
